/**
 * TraineeAssessmentView Component
 * ORCA360 - Trainee Read-Only Assessment View
 *
 * Displays complete assessment with acknowledgment form
 * Features: Read-only competency scores, evidence, analysis, goals, PDF download, acknowledgment
 *
 * Sprint 4, Day 6-7: Trainee Portal Implementation
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExportOverallResult, fetchServerFocusList, formatDateForFilename, GRADE_LABEL_BY_VALUE, overallResultForExport, pilotFunctionForExport, resolveFocusRows, type FocusListDataClient } from '@orca360/shared';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { usePDFExportPilotAssessment } from '../hooks/usePDFExportPilotAssessment';
import {
  mapAssessmentsToPartC,
  mapAssessmentsToPartD,
  extractTEMFromAssessments,
  type PilotAssessmentExportData,
  type AssessmentPilotRow,
} from '../types/pilot-assessment-export';
import type { DebriefingPartAData } from '../types/debriefing-export';
import { useAuditLog } from '../hooks/useAuditLog';
import { PILOT_COMPETENCY_FULL_NAMES } from '@orca360/shared';
// ORCA360 v2 Assessment Components
import { 
  ORCA360PartA, 
  ORCA360PartB, 
  ORCA360PartC, 
  ORCA360PartD,
  type PartAData,
  type PartBData,
  type PartCData,
  type PartDData,
} from '../components/assessment';

interface Evidence {
  quote: string;
  timestamp: string;
  validation_status: string;
  confidence?: number;
  obCode?: string;
  obCodeSource?: 'llm' | 'pattern_match' | 'fallback';
}

interface Assessment {
  id: string;
  session_id: string;
  trainee_id: string;
  competency: string;
  score: number;
  evidence: Evidence[];
  analysis: string;
  goals: string;
  status: 'draft' | 'published';
  version: number;
  created_at: string;
  updated_at: string;
  // The Step 6 capture record (migration 248), declared so Part C can be built
  // from it without a cast (HG-3B G3-04.1).
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  override_actor?: string | null;
  override_at?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
  development_level_assigned_at?: string | null;
  historical?: boolean | null;
}

interface SessionInfo {
  trainee_name: string;
  instructor_name: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  session_type: string;
  pilot_role?: 'captain' | 'first_officer';
  overall_result?: ExportOverallResult;
}

interface Acknowledgment {
  id: string;
  session_id: string;
  user_id: string;
  acknowledged_at: string;
  self_reflection?: string;
}

// ORCA360 v2 Assessment Data
interface ORCA360V2Assessment {
  partA: PartAData;
  partB: PartBData;
  partC: PartCData;
  partD: PartDData;
}

// Single source: shared framework module (long-form display names).
const COMPETENCY_NAMES: Record<string, string> = PILOT_COMPETENCY_FULL_NAMES;

export default function TraineeAssessmentView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [acknowledgment, setAcknowledgment] = useState<Acknowledgment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selfReflection, setSelfReflection] = useState('');
  const [agreedToAcknowledge, setAgreedToAcknowledge] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [v2Assessment, setV2Assessment] = useState<ORCA360V2Assessment | null>(null);
  const [showV2View, setShowV2View] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const { logAction } = useAuditLog();
  const { exportPilotAssessmentToPDF, isExporting } = usePDFExportPilotAssessment({
    onExportSuccess: (blob, filename) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      alert('PDF downloaded successfully!');
    },
    onExportError: (err) => {
      alert(`Failed to download PDF: ${err.message}`);
    },
  });

  useEffect(() => {
    loadAssessmentData();
  }, [sessionId]);

  const loadAssessmentData = async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Not authenticated');
        return;
      }
      setUserId(user.id);

      console.log('[TraineeAssessmentView] Loading session:', sessionId);

      // Load session info
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          session_date,
          aircraft_type,
          flight_phase,
          trainee:trainee_id(full_name),
          instructor:instructor_id(full_name),
          session_type:session_type_id(name),
          overall_result
        `)
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;
      if (!session) {
        setError('Session not found');
        return;
      }

      const instructorData = Array.isArray(session.instructor) ? session.instructor[0] : session.instructor;
      const sessionTypeData = Array.isArray(session.session_type) ? session.session_type[0] : session.session_type;

      const { data: userProfile } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();

      const { data: crewData } = await supabase
        .from('session_crew')
        .select('crew_role')
        .eq('session_id', sessionId)
        .eq('user_id', user.id)
        .maybeSingle();

      setSessionInfo({
        trainee_name: userProfile?.full_name || 'Unknown',
        instructor_name: instructorData?.full_name || 'Unknown',
        session_date: session.session_date,
        aircraft_type: session.aircraft_type,
        flight_phase: session.flight_phase || 'Initial Training',
        session_type: sessionTypeData?.name || 'Simulator',
        pilot_role: crewData?.crew_role || undefined,
        overall_result: session.overall_result,
      });

      // Load assessments
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessments_pilot')
        .select('*')
        .eq('session_id', sessionId)
        .eq('trainee_id', user.id)
        .eq('status', 'published')
        .order('competency', { ascending: true });

      if (assessmentError) throw assessmentError;

      if (!assessmentData || assessmentData.length === 0) {
        setError('No published assessments found for this session');
        return;
      }

      setAssessments(assessmentData);

      // Load acknowledgment if exists
      const { data: ackData, error: ackError } = await supabase
        .from('acknowledgments')
        .select('*')
        .eq('session_id', sessionId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (ackError && ackError.code !== 'PGRST116') { // Ignore "not found" error
        console.error('[TraineeAssessmentView] Error loading acknowledgment:', ackError);
      }

      if (ackData) {
        setAcknowledgment(ackData);
        setSelfReflection(ackData.self_reflection || '');
      }

      // Load ORCA360 v2 partA (session snapshot) and partB (TEM) from orca360_assessments.
      // partC and partD are deliberately NOT read here — those JSONB blobs are AI snapshots
      // that go stale the moment an instructor edits a score, analysis, or goal in
      // assessments_pilot. We derive them from the live assessments_pilot rows below so the
      // trainee sees the same data as the editor and the mobile app.
      const { data: v2Data, error: v2Error } = await supabase
        .from('orca360_assessments')
        .select('part_a, part_b')
        .eq('session_id', sessionId)
        .eq('trainee_id', user.id)
        .maybeSingle();

      if (v2Error && v2Error.code !== 'PGRST116') {
        console.warn('[TraineeAssessmentView] Error loading v2 assessment:', v2Error);
      }

      if (v2Data) {
        // Derive Part C from live assessments_pilot rows
        const partC: PartCData = {
          assessments: assessmentData.map((a) => ({
            competency: a.competency,
            competencyName: COMPETENCY_NAMES[a.competency] || a.competency,
            grade: a.score,
            observableBehaviors: (a.evidence || []).map((e: Evidence, idx: number) => ({
              ob: {
                code: e.obCode || `${a.competency} ${idx + 1}.0`,
                description: '',
              },
              comment: e.quote,
              quote: e.quote,
              timestamp: e.timestamp,
              validationStatus: e.validation_status,
            })),
            instructorComments: a.analysis,
            // GCH-14 / GAT-03: the dimensions, the limiting factor and the
            // override record travel with the grade into the trainee's report.
            // GAT-11: a historical row shows every one of them as not recorded.
            capture: {
              how_many: a.how_many ?? null,
              how_often: a.how_often ?? null,
              tem_outcome: a.tem_outcome ?? null,
              limiting_factor: a.limiting_factor ?? null,
              proposed_grade: a.proposed_grade ?? null,
              override_flag: a.override_flag ?? null,
              override_category: a.override_category ?? null,
              override_note: a.override_note ?? null,
              override_actor: a.override_actor ?? null,
              override_at: a.override_at ?? null,
              development_level: a.development_level ?? null,
              development_level_source: a.development_level_source ?? null,
              development_level_assigned_at: a.development_level_assigned_at ?? null,
              historical: a.historical ?? null,
            },
          })),
          gradeLegend: GRADE_LABEL_BY_VALUE,
        };

        // Part D (focus competencies) from the live assessments_pilot rows,
        // never from the stale part_d snapshot (see above). G3-06: the server
        // list of migration 258 decides membership and order and is rendered
        // verbatim; where it could not be read, the GAT-04 client fallback
        // selects grades 1 and 2 only.
        const serverFocusList = await fetchServerFocusList(supabase as unknown as FocusListDataClient, sessionId);
        const focusAssessments = resolveFocusRows(serverFocusList, assessmentData, {
          sortWorstFirst: true,
          max: 4,
        }).items;

        const partD: PartDData = {
          focusCompetencies: focusAssessments.map((a, idx) => ({
            focusNumber: idx + 1,
            competency: a.competency,
            competencyName: COMPETENCY_NAMES[a.competency] || a.competency,
            causalFactor: a.analysis || 'Root cause analysis pending.',
            recommendations: a.goals || 'SMART goals pending generation.',
            evidence: (a.evidence || []).map((e: Evidence) => `[${e.timestamp}] "${e.quote}"`),
          })),
          overallStrategy:
            focusAssessments.length > 0
              ? `Focus on ${focusAssessments
                  .map((a) => COMPETENCY_NAMES[a.competency] || a.competency)
                  .join(', ')} in upcoming training sessions.`
              : 'All competencies at or above standard. Continue current training approach.',
        };

        setV2Assessment({
          partA: v2Data.part_a,
          partB: v2Data.part_b,
          partC,
          partD,
        });
        setShowV2View(true); // Default to v2 view if available
        console.log('[TraineeAssessmentView] Loaded ORCA360 v2 assessment (partC/partD derived from assessments_pilot)');
      }

      console.log('[TraineeAssessmentView] Loaded', assessmentData.length, 'assessments');

    } catch (err: any) {
      console.error('[TraineeAssessmentView] Error:', err);
      setError(err.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const buildExportData = (): PilotAssessmentExportData => {
    const rows = assessments as unknown as AssessmentPilotRow[];
    const pilotRole = sessionInfo?.pilot_role ?? 'first_officer';
    const pilotFunction = pilotFunctionForExport(pilotRole);

    const partA: DebriefingPartAData = {
      trainee: { name: sessionInfo?.trainee_name ?? '', id: userId ?? '' },
      instructor: { name: sessionInfo?.instructor_name ?? '', id: '' },
      aircraftType: sessionInfo?.aircraft_type ?? '',
      sessionDate: sessionInfo?.session_date
        ? formatDateForFilename(sessionInfo.session_date)
        : '',
      startTime: '',
      endTime: '',
      timeAsPF: { hours: 0, minutes: 0 },
      timeAsPM: { hours: 0, minutes: 0 },
      location: '',
      simulatorId: '',
      sessionName: `Session ${sessionId ?? ''}`,
      sessionType: sessionInfo?.session_type,
      flightPhase: sessionInfo?.flight_phase,
      overallResult: overallResultForExport(sessionInfo?.overall_result),
      pilotFunction,
    };

    return {
      partA,
      partB: extractTEMFromAssessments(rows),
      partC: mapAssessmentsToPartC(rows),
      partD: mapAssessmentsToPartD(rows),
      metadata: {
        generatedAt: new Date().toISOString(),
        version: '1.0',
        traineeId: userId ?? '',
        pilotRole,
      },
    };
  };

  const handleDownloadPDF = async () => {
    if (!assessments.length || !sessionInfo) return;

    try {
      await exportPilotAssessmentToPDF(buildExportData(), sessionId);

      await logAction({
        action: 'assessment_exported',
        entity_type: 'assessment_pilot',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          exported_by: 'trainee',
          competencies: assessments.map(a => a.competency).join(', '),
        },
      });
    } catch (err) {
      console.error('[TraineeAssessmentView] PDF export error:', err);
    }
  };

  const handleSubmitAcknowledgment = async () => {
    if (!sessionId || !agreedToAcknowledge) return;

    try {
      setSubmitting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Not authenticated');
        return;
      }

      // Insert acknowledgment
      const { error: insertError } = await supabase
        .from('acknowledgments')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          acknowledged_at: new Date().toISOString(),
          self_reflection: selfReflection.trim() || null,
        });

      if (insertError) throw insertError;

      // Log acknowledgment action
      await logAction({
        action: 'assessment_acknowledged',
        entity_type: 'acknowledgment',
        entity_id: sessionId,
        metadata: {
          session_id: sessionId,
          has_reflection: !!selfReflection.trim(),
        },
      });

      // Reload data to show acknowledged state
      await loadAssessmentData();

      alert('Assessment acknowledged successfully!');

    } catch (err: any) {
      console.error('[TraineeAssessmentView] Acknowledgment error:', err);
      alert(`Failed to acknowledge assessment: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  /** Canonical label for a 1 to 5 grade; averages round to the nearest grade. */
  const gradeLabel = (grade: number): string =>
    GRADE_LABEL_BY_VALUE[Math.min(5, Math.max(1, Math.round(grade))) as 1 | 2 | 3 | 4 | 5];

  /**
   * GAT-04: the grade 3 band is neutral. Grade 3 Adequate meets the required
   * standard, so it never shares the warning colour used below it.
   */
  const getScoreColor = (score: number): string => {
    if (score >= 4.5) return 'bg-green-500';
    if (score >= 3.5) return 'bg-blue-500';
    if (score >= 2.5) return 'bg-slate-400';
    if (score >= 1.5) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getValidationIcon = (status: string): string => {
    switch (status) {
      case 'VERIFIED': return '✓';
      case 'PARAPHRASED': return '⚠';
      case 'SIMILAR': return '⚠';
      case 'UNVERIFIED': return '❌';
      default: return '';
    }
  };

  const getValidationColor = (status: string): string => {
    switch (status) {
      case 'VERIFIED': return 'text-green-700 bg-green-100';
      case 'PARAPHRASED': return 'text-yellow-700 bg-yellow-100';
      case 'SIMILAR': return 'text-orange-700 bg-orange-100';
      case 'UNVERIFIED': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const avgScore = assessments.length > 0
    ? assessments.reduce((sum, a) => sum + a.score, 0) / assessments.length
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orca-elevate"></div>
          <p className="mt-4 text-steel">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ice">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={() => navigate('/trainee/dashboard')}
              className="mt-3 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ice">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/trainee/dashboard')}
            className="text-orca-elevate hover:text-orca-elevate/80 text-sm font-medium mb-3"
          >
            ← Back to Dashboard
          </button>
          
          <div className="bg-white rounded-lg shadow-md p-5">

            {/* Session identity — replaces generic title */}
            <h1 className="text-lg font-bold text-navy leading-snug">
              {sessionInfo?.aircraft_type}
              <span className="text-steel font-normal"> · </span>
              {sessionInfo?.flight_phase}
            </h1>

            {/* Compact meta line */}
            <p className="text-sm text-steel mt-0.5 mb-4">
              {sessionInfo && format(new Date(sessionInfo.session_date), 'MMM dd, yyyy')}
              {' · '}
              {sessionInfo?.instructor_name}
              {sessionInfo?.pilot_role && ` · ${sessionInfo.pilot_role === 'captain' ? 'Captain' : 'First Officer'}`}
            </p>

            {/* Score hero */}
            <div className="flex flex-col items-center py-3 mb-4 bg-ice rounded-lg">
              <div className="text-xs font-semibold text-steel uppercase tracking-wide mb-1">Overall Grade</div>
              <div className="text-5xl font-bold text-navy">{avgScore.toFixed(1)}</div>
              <div className="text-sm text-steel mt-1">{gradeLabel(avgScore)}</div>
            </div>

            {/* Action buttons — full-width on mobile */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleDownloadPDF}
                disabled={isExporting}
                className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-navy bg-white border border-orca-main hover:bg-navy hover:text-white rounded-md transition-colors disabled:opacity-50"
              >
                {isExporting ? 'Generating PDF...' : '📄 Download PDF'}
              </button>

              {v2Assessment && (
                <button
                  onClick={() => setShowV2View(!showV2View)}
                  className={`w-full sm:w-auto px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                    showV2View
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'text-blue-600 bg-white border border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {showV2View ? '📊 ORCA360 View' : '📋 Classic View'}
                </button>
              )}
            </div>

            {acknowledgment && (
              <div className="mt-3 text-sm text-green-600 font-medium text-center">
                ✓ Acknowledged on {format(new Date(acknowledgment.acknowledged_at), 'MMM dd, yyyy · h:mm a')}
              </div>
            )}

          </div>
        </div>

        {/* ORCA360 v2 Assessment View */}
        {showV2View && v2Assessment ? (
          <div className="space-y-6 mb-6">
            <ORCA360PartA data={v2Assessment.partA} />
            <ORCA360PartB data={v2Assessment.partB} />
            <ORCA360PartC data={v2Assessment.partC} />
            <ORCA360PartD data={v2Assessment.partD} />
          </div>
        ) : (
        /* Classic Competency Assessments View */
        <div className="space-y-4 mb-6">
          {assessments.map((assessment) => (
            <div key={assessment.id} className="bg-white rounded-lg shadow-md p-6">
              {/* Competency Header */}
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-3 border-b border-steel/20">
                <h3 className="text-lg font-semibold text-navy min-w-0 flex-1">
                  {COMPETENCY_NAMES[assessment.competency]} ({assessment.competency})
                </h3>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className="text-xs text-steel">Grade</div>
                    <div className="text-2xl font-bold text-navy">{assessment.score}/5</div>
                    <div className="text-xs text-steel">{gradeLabel(assessment.score)}</div>
                  </div>
                  <div className={`w-3 h-12 rounded ${getScoreColor(assessment.score)}`}></div>
                </div>
              </div>

              {/* Evidence */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-navy mb-2">Evidence</h4>
                <div className="space-y-2">
                  {assessment.evidence && assessment.evidence.length > 0 ? (
                    assessment.evidence.map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-md border-l-4 ${
                          item.validation_status === 'VERIFIED' || item.validation_status === 'VERBATIM'
                            ? 'border-green-500 bg-green-50'
                            : item.validation_status === 'PARAPHRASED'
                            ? 'border-yellow-500 bg-yellow-50'
                            : item.validation_status === 'SIMILAR'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-red-500 bg-red-50'
                        }`}
                      >
                        <div className="flex flex-wrap items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-navy mb-1 line-clamp-3">"{item.quote}"</p>
                            <p className="text-xs text-steel">@ {item.timestamp}</p>
                          </div>
                          <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded ${getValidationColor(item.validation_status)}`}>
                            {getValidationIcon(item.validation_status)} {item.validation_status}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-steel italic">No evidence recorded</p>
                  )}
                </div>
              </div>

              {/* Analysis */}
              {assessment.analysis && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-navy mb-2">Root Cause Analysis</h4>
                  <div className="bg-steel/5 rounded-md p-3">
                    <p className="text-sm text-navy whitespace-pre-wrap">{assessment.analysis}</p>
                  </div>
                </div>
              )}

              {/* Goals */}
              {assessment.goals && (
                <div>
                  <h4 className="text-sm font-semibold text-navy mb-2">Improvement Goals</h4>
                  <div className="bg-blue-50 rounded-md p-3">
                    <p className="text-sm text-navy whitespace-pre-wrap">{assessment.goals}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {/* Acknowledgment Section */}
        {!acknowledgment && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-navy mb-4">Acknowledge Assessment</h3>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-steel mb-2 block">
                Self-Reflection (Optional)
              </label>
              <textarea
                value={selfReflection}
                onChange={(e) => setSelfReflection(e.target.value)}
                placeholder="Share your thoughts on this assessment, what you learned, or areas you'd like to focus on..."
                className="w-full h-24 px-3 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
                maxLength={2000}
              />
              <div className="text-xs text-steel mt-1">
                {selfReflection.length}/2000 characters
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToAcknowledge}
                  onChange={(e) => setAgreedToAcknowledge(e.target.checked)}
                  className="mt-1 w-4 h-4 text-orca-elevate border-gray-300 rounded focus:ring-orca-elevate"
                />
                <span className="text-sm text-navy">
                  I acknowledge that I have reviewed this assessment and understand the feedback provided. 
                  I will work on the improvement goals identified.
                </span>
              </label>
            </div>

            <button
              onClick={handleSubmitAcknowledgment}
              disabled={!agreedToAcknowledge || submitting}
              className="w-full px-6 py-3 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Acknowledgment'}
            </button>

            <p className="text-xs text-steel mt-3 text-center">
              You must check the box above to submit your acknowledgment
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

